import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { emailPreferencesService } from '../../../services/emailPreferencesService';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const TIMEZONE_OPTIONS = [
  { value: 'America/Barbados', label: 'Atlantic Time - Barbados (UTC-4)' },
  { value: 'America/New_York', label: 'Eastern Time - New York (UTC-5/UTC-4)' },
  { value: 'America/Chicago', label: 'Central Time - Chicago (UTC-6/UTC-5)' },
  { value: 'America/Denver', label: 'Mountain Time - Denver (UTC-7/UTC-6)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time - Los Angeles (UTC-8/UTC-7)' },
  { value: 'America/Toronto', label: 'Eastern Time - Toronto (UTC-5/UTC-4)' },
  { value: 'America/Vancouver', label: 'Pacific Time - Vancouver (UTC-8/UTC-7)' },
  { value: 'Europe/London', label: 'London (UTC+0/UTC+1)' },
  { value: 'Europe/Paris', label: 'Paris (UTC+1/UTC+2)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (UTC+8)' },
  { value: 'Australia/Sydney', label: 'Sydney (UTC+10/UTC+11)' },
];

const BUDGETING_METHOD_OPTIONS = [
  { value: 'traditional', label: 'Traditional Budgeting' },
  { value: 'zero_based', label: 'Zero-Based Budgeting' },
  { value: '50_30_20', label: '50/30/20 Rule' },
  { value: 'pay_yourself_first', label: 'Pay Yourself First' }
];

const PreferencesSection = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState({
    budgetAlerts: true,
    transactionNotifications: true,
    goalReminders: true,
    weeklyReports: false,
    timezone: 'America/Barbados',
    budgetingMethod: 'traditional',
    businessMode: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      // Load localStorage preferences for non-email settings
      const savedPreferences = localStorage.getItem('userPreferences');
      if (savedPreferences) {
        const parsed = JSON.parse(savedPreferences);
        setPreferences(prev => ({
          ...prev,
          budgetAlerts: parsed?.budgetAlerts ?? true,
          transactionNotifications: parsed?.transactionNotifications ?? true,
          goalReminders: parsed?.goalReminders ?? true
        }));
      }

      // Load email preferences from database
      const emailPrefs = await emailPreferencesService?.getPreferences();
      setPreferences(prev => ({
        ...prev,
        weeklyReports: emailPrefs?.weeklyDigestEnabled || false
      }));
      
      // Load timezone, budgeting method, and business mode from user profile
      if (user) {
        const { data: profile, error } = await supabase?.from('user_profiles')?.select('timezone, budgeting_method, business_mode')?.eq('id', user?.id)?.single();
        
        if (!error && profile) {
          setPreferences(prev => ({
            ...prev,
            timezone: profile?.timezone || 'America/Barbados',
            budgetingMethod: profile?.budgeting_method || 'traditional',
            businessMode: profile?.business_mode || false
          }));
        }
      }
    } catch (err) {
      console.error('Error loading preferences:', err);
      // Continue with default values if error
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Save non-email preferences to localStorage
      localStorage.setItem('userPreferences', JSON.stringify({
        budgetAlerts: preferences?.budgetAlerts,
        transactionNotifications: preferences?.transactionNotifications,
        goalReminders: preferences?.goalReminders
      }));

      // Save email preferences to database
      await emailPreferencesService?.updatePreferences({
        weeklyDigestEnabled: preferences?.weeklyReports,
        digestDayOfWeek: 1 // Monday
      });
      
      // Save timezone, budgeting method, and business mode to user profile
      if (user) {
        const { error: profileError } = await supabase?.from('user_profiles')?.update({ 
          timezone: preferences?.timezone,
          budgeting_method: preferences?.budgetingMethod,
          business_mode: preferences?.businessMode
        })?.eq('id', user?.id);
        
        if (profileError) throw profileError;
      }

      setSuccess('Preferences saved successfully');
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError(err?.message || 'Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-20 md:p-24">
      <div className="flex items-center gap-12 mb-20">
        <Icon name="Settings" size={24} color="var(--color-primary)" />
        <h2 className="text-xl font-heading font-semibold text-card-foreground">Preferences</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-24">
        Customize your application preferences and notification settings
      </p>

      {success && (
        <div className="mb-20 p-16 bg-success/10 border border-success/20 rounded-lg flex items-start gap-12">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" />
          <p className="text-sm text-success flex-1">{success}</p>
        </div>
      )}

      {error && (
        <div className="mb-20 p-16 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-12">
          <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
          <p className="text-sm text-destructive flex-1">{error}</p>
        </div>
      )}

      <div className="space-y-32">
        <div>
          <h3 className="text-sm font-caption font-semibold text-foreground mb-16">Date & Time</h3>
          <div className="space-y-16">
            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Timezone
              </label>
              <p className="text-xs text-muted-foreground mb-12">
                Your selected timezone is used for all dates across transactions, bills, goals, insights, and reports. This ensures consistent date handling and prevents automatic date shifting.
              </p>
              <Select
                value={preferences?.timezone}
                onChange={(e) => setPreferences({ ...preferences, timezone: e?.target?.value })}
                options={TIMEZONE_OPTIONS}
              />
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-caption font-semibold text-foreground mb-16">Budgeting</h3>
          <div className="space-y-16">
            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Budgeting Method
              </label>
              <p className="text-xs text-muted-foreground mb-12">
                Choose your preferred budgeting method. The budget page layout will adapt based on your selection. You can change this at any time.
              </p>
              <Select
                value={preferences?.budgetingMethod}
                onChange={(e) => setPreferences({ ...preferences, budgetingMethod: e?.target?.value })}
                options={BUDGETING_METHOD_OPTIONS}
              />
            </div>
            <Checkbox
              label="Business Mode"
              description="Enable tracking for side hustles and small business income/expenses (not a replacement for full accounting software)"
              checked={preferences?.businessMode}
              onChange={(e) => setPreferences({ ...preferences, businessMode: e?.target?.checked })}
            />
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-caption font-semibold text-foreground mb-16">Notifications</h3>
          <div className="space-y-16">
            <Checkbox
              label="Budget Alerts"
              description="Receive alerts when approaching budget limits"
              checked={preferences?.budgetAlerts}
              onChange={(e) => setPreferences({ ...preferences, budgetAlerts: e?.target?.checked })}
            />
            <Checkbox
              label="Transaction Notifications"
              description="Get notified about new transactions"
              checked={preferences?.transactionNotifications}
              onChange={(e) => setPreferences({ ...preferences, transactionNotifications: e?.target?.checked })}
            />
            <Checkbox
              label="Goal Reminders"
              description="Receive reminders about financial goals"
              checked={preferences?.goalReminders}
              onChange={(e) => setPreferences({ ...preferences, goalReminders: e?.target?.checked })}
            />
            <Checkbox
              label="Weekly Email Digest"
              description="Receive weekly email summaries with spending insights, category breakdowns, and budget alerts"
              checked={preferences?.weeklyReports}
              onChange={(e) => setPreferences({ ...preferences, weeklyReports: e?.target?.checked })}
            />
          </div>
        </div>

        <div className="flex justify-end pt-16 border-t border-border">
          <Button
            variant="default"
            loading={loading}
            onClick={handleSave}
            iconName="Save"
            iconPosition="left"
          >
            Save Preferences
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PreferencesSection;